/*
  Sticky Kaufhilfe auf Produktseiten.
  Desktop: Karte unten rechts. Mobil: Leiste am unteren Bildschirmrand.

  Sie ist ein Spiegel des echten Kaufbereichs, kein zweiter Kaufpfad: Preis und
  Button-Zustand werden aus der Hauptsektion uebernommen, der Klick wird an den
  echten Submit-Button weitergereicht. Dadurch funktionieren Varianten-Wechsel,
  Mengenregeln, Cart-Drawer und App-Handler unveraendert.
*/
class StickyAtc extends HTMLElement {
  connectedCallback() {
    this.priceTarget = this.querySelector('[data-sticky-atc-price]');
    this.button = this.querySelector('[data-sticky-atc-submit]');
    this.buttonLabel = this.button.querySelector('[data-sticky-atc-label]');
    this.panel = this.querySelector('[data-sticky-atc-panel]');
    this.panelInner = this.querySelector('[data-sticky-atc-panel-inner]');
    this.nav = this.querySelector('[data-sticky-atc-nav]');
    this.navList = this.querySelector('[data-sticky-atc-nav-list]');
    this.quantity = this.querySelector('[data-sticky-atc-quantity]');
    this.quantityValue = this.querySelector('[data-sticky-atc-quantity-value]');
    this.quantitySteps = [...this.querySelectorAll('[data-sticky-atc-quantity-step]')];

    this.onSubmit = this.onSubmit.bind(this);
    this.onQuantityStep = this.onQuantityStep.bind(this);
    this.onSourceMutation = this.onSourceMutation.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onBreakpoint = this.onBreakpoint.bind(this);
    this.onOutside = this.onOutside.bind(this);
    this.onKey = this.onKey.bind(this);

    this.button.addEventListener('click', this.onSubmit);
    this.quantitySteps.forEach((knopf) => knopf.addEventListener('click', this.onQuantityStep));
    this.querySelector('[data-sticky-atc-close]')?.addEventListener('click', () => this.togglePanel(false));
    this.variantLine = this.querySelector('[data-sticky-atc-variant]');
    this.variantText = this.querySelector('[data-sticky-atc-variant-text]');
    this.querySelectorAll('[data-sticky-atc-panel-toggle]').forEach((knopf) =>
      knopf.addEventListener('click', () => this.togglePanel(!this.panelOpen))
    );

    // Bewusst ohne Abbruch, wenn der Kaufbereich jetzt noch nicht steht: Apps
    // schieben ihre Buttons teils erst spaeter nach. Die Quelle wird deshalb
    // bei jeder Pruefung neu gesucht, bis sie da ist.
    this.resolveSource();
    this.sync();
    this.buildNav();
    this.watchVisibility();
  }

  disconnectedCallback() {
    this.sourceObserver?.disconnect();
    this.layoutObserver?.disconnect();
    this.infoObserver?.disconnect();
    this.rootObserver?.disconnect();
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    this.desktop?.removeEventListener('change', this.onBreakpoint);
    document.removeEventListener('click', this.onOutside);
    document.removeEventListener('keydown', this.onKey);
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
  }

  /* ---------- Quellen im Kaufbereich ---------- */

  resolveSource() {
    this.productInfo = document.querySelector('product-info[id^="MainProduct-"]');
    this.info = this.productInfo?.querySelector('.product__info-container') || this.productInfo;
    // Im Kaufbereich koennen mehrere Submit-Buttons stehen: Klaviyo legt seinen
    // "Benachrichtigen"-Button mit derselben Klasse davor und blendet ihn aus,
    // solange das Produkt lieferbar ist. Gespiegelt wird der erste sichtbare.
    this.realButton = [...(this.info?.querySelectorAll('.product-form__submit') || [])].find(isRendered);
    // Ohne sichtbaren Kaufbutton gibt es nichts zu spiegeln.
    if (!this.realButton) return false;

    this.priceSource = this.info.querySelector('[id^="price-"]') || this.info.querySelector('.price');
    this.anchor = this.realButton.closest('.product-form__buttons') || this.realButton;

    // Menge und Varianten werden nicht nachgebaut, sondern bedient: die Karte
    // setzt die echten Felder und laesst das Theme rechnen.
    this.variantSource = this.info.querySelector('variant-selects');
    this.quantitySource = this.info.querySelector('.quantity__input, input[name="quantity"]');
    this.quantityWidget = this.quantitySource?.closest('quantity-input');
    return true;
  }

  get sourceIsStale() {
    return !this.realButton?.isConnected || !isRendered(this.realButton);
  }

  /* ---------- Spiegeln ---------- */

  sync() {
    if (this.sourceIsStale && !this.resolveSource()) return;
    this.watchSource();
    this.syncPrice();
    this.syncQuantity();
    // Vor syncButton: dessen Beschriftung haengt davon ab, ob es eine Auswahl
    // gibt — sonst steht beim ersten Rendern noch der Kauf-Text da.
    this.syncPanel();
    this.syncButton();
  }

  syncPrice() {
    if (!this.priceSource || !this.priceTarget) return;
    this.priceTarget.innerHTML = this.priceSource.innerHTML;
    // Geklonte IDs und Live-Region-Rollen wuerden sich mit dem Original doppeln.
    this.priceTarget.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
    this.priceTarget.querySelectorAll('[role]').forEach((node) => node.removeAttribute('role'));
  }

  syncButton() {
    if (!this.realButton) return;
    this.button.disabled = this.realButton.hasAttribute('disabled');
    const label = this.realButton.querySelector('span:not(.visually-hidden)') || this.realButton;
    // Auf der Produktseite ist immer eine Variante vorgewaehlt — der Knopf
    // kann deshalb direkt legen und muss nicht erst ins Panel fuehren.
    this.buttonLabel.textContent = label.textContent.trim();

    // Die gewaehlte Variante ist zugleich der Zugang zum Panel.
    // value statt text: die Anzeige im Dropdown traegt noch den
    // Verfuegbarkeitszusatz ("orange / schwarz – verfuegbar"), der in der
    // Zeile nichts zu suchen hat.
    const auswahl = (this.variantGroups || [])
      .filter((g) => g.istVariante !== false)
      .map((g) => g.values.find((w) => w.selected)?.value)
      .filter(Boolean)
      .join(' / ');
    if (this.variantLine) {
      this.variantLine.hidden = !auswahl;
      this.variantText.textContent = auswahl;
    }
    this.querySelectorAll('[data-sticky-atc-panel-toggle]').forEach((k) =>
      k.setAttribute('aria-expanded', String(!!this.panelOpen))
    );
  }

  /* ---------- Menge ---------- */

  // Gedrueckt werden die echten Plus/Minus-Knoepfe: Mindestmengen und
  // Schrittweiten des Themes gelten damit unveraendert.
  syncQuantity() {
    const vorhanden = !!this.quantitySource;
    this.quantity.hidden = !vorhanden;
    if (!vorhanden) return;
    this.quantityValue.textContent = this.quantitySource.value;
    this.quantitySteps.forEach((knopf) => {
      const richtung = Number(knopf.dataset.stickyAtcQuantityStep);
      knopf.disabled = richtung < 0 && Number(this.quantitySource.value) <= Number(this.quantitySource.min || 1);
    });
  }

  onQuantityStep(event) {
    if (this.sourceIsStale && !this.resolveSource()) return;
    if (!this.quantitySource) return;
    const richtung = Number(event.currentTarget.dataset.stickyAtcQuantityStep);
    const echter = this.quantityWidget?.querySelector(`button[name="${richtung < 0 ? 'minus' : 'plus'}"]`);
    if (echter) {
      echter.click();
    } else {
      const schritt = Number(this.quantitySource.step) || 1;
      const min = Number(this.quantitySource.min) || 1;
      this.quantitySource.value = Math.max(min, Number(this.quantitySource.value) + richtung * schritt);
      this.quantitySource.dispatchEvent(new Event('change', { bubbles: true }));
    }
    this.syncQuantity();
  }

  onSubmit() {
    if (this.sourceIsStale && !this.resolveSource()) return;
    this.realButton.click();
  }

  /* ---------- Panel mit der Variantenauswahl ---------- */

  get hasVariants() {
    return this.variantGroups?.length > 0;
  }

  // Liest ALLE Auswahlfelder des Kaufbereichs — nicht nur die Shopify-Varianten
  // in <variant-selects>. Das Theme rendert eigene Konfiguratorfelder daneben
  // (z. B. "Auszugslaenge"), die genauso zur Kaufentscheidung gehoeren.
  // Dropdowns und Radiogruppen (Pillen/Swatches) werden beide als Dropdown
  // gespiegelt und beim Wechsel am Original bedient.
  readVariantGroups() {
    if (!this.info) return [];
    const gruppen = [];
    // Nach einem Markup-Tausch stehen Bloecke kurzzeitig doppelt im Dokument —
    // je Feldnamen nur einmal spiegeln.
    const gesehen = new Set();

    this.info.querySelectorAll('.product-form__input').forEach((block) => {
      // Die Menge hat ihre eigene Steuerung in der Karte.
      if (block.classList.contains('product-form__quantity')) return;

      const feld = block.querySelector('select');
      const knoepfe = [...block.querySelectorAll('input[type="radio"]')];
      // Nur die erste Zeile: an den Beschriftungen haengen Info-Texte und
      // Tooltips, die im Panel den ganzen Platz fressen wuerden.
      const beschriftung = kuerze(
        block
          .querySelector('legend, label')
          ?.textContent.split('\n')
          .map((z) => z.trim())
          .find(Boolean)
      );

      const istVariante = !!block.closest('variant-selects');

      if (feld) {
        const name = feld.name;
        if (gesehen.has(name)) return;
        gesehen.add(name);
        gruppen.push({
          key: name,
          istVariante,
          label: beschriftung || name,
          values: [...feld.options].map((o) => ({
            value: o.value,
            text: o.textContent.replace(/\s+/g, ' ').trim(),
            selected: o.selected,
            disabled: o.disabled,
          })),
          // Bewusst zur Klickzeit neu gesucht: das Theme tauscht bei jeder
          // Auswahl sein Markup aus, ein gemerktes Feld waere danach los-
          // geloest und die zweite Auswahl liefe ins Leere.
          apply: (wert) => {
            const aktuell = this.info?.querySelector(`select[name="${CSS.escape(name)}"]`);
            if (!aktuell) return;
            aktuell.value = wert;
            aktuell.dispatchEvent(new Event('change', { bubbles: true }));
          },
        });
        return;
      }

      if (!knoepfe.length) return;
      const name = knoepfe[0].name;
      if (gesehen.has(name)) return;
      gesehen.add(name);
      gruppen.push({
        key: name,
        istVariante,
        label: beschriftung || name,
        values: knoepfe.map((k) => ({
          value: k.value,
          text: sichtbarerText(block.querySelector(`label[for="${k.id}"]`)) || k.value,
          selected: k.checked,
          disabled: k.disabled || k.classList.contains('disabled'),
        })),
        apply: (wert) => {
          const aktuelle = [...(this.info?.querySelectorAll(`input[type="radio"][name="${CSS.escape(name)}"]`) || [])];
          aktuelle.find((k) => k.value === wert)?.click();
        },
      });
    });

    return gruppen;
  }

  syncPanel() {
    this.variantGroups = this.readVariantGroups();
    if (!this.hasVariants && !this.staffelSource) {
      this.panel.hidden = true;
      this.panelInner.replaceChildren();
      this.panelOpen = false;
      return;
    }

    const gleicheForm =
      this.panelFields?.length === this.variantGroups.length &&
      this.panelFields.every((feld, i) => feld.options.length === this.variantGroups[i].values.length);

    // Im laufenden Betrieb nur die Zustaende nachziehen — ein Neuaufbau wuerde
    // dem Nutzer den Fokus aus dem gerade bedienten Feld nehmen.
    if (gleicheForm) {
      this.panelFields.forEach((feld, i) => {
        this.variantGroups[i].values.forEach((wert, j) => {
          feld.options[j].disabled = wert.disabled;
          feld.options[j].selected = wert.selected;
        });
      });
      return;
    }

    this.panelInner.replaceChildren();
    this.panelFields = this.variantGroups.map((gruppe) => {
      const zeile = document.createElement('div');
      zeile.className = 'sticky-atc__field';
      const label = document.createElement('label');
      label.className = 'form__label';
      label.textContent = gruppe.label;
      const feld = document.createElement('select');
      feld.className = 'select__select';
      gruppe.values.forEach((wert) => {
        const option = document.createElement('option');
        option.value = wert.value;
        option.textContent = wert.text;
        option.disabled = wert.disabled;
        option.selected = wert.selected;
        feld.appendChild(option);
      });
      feld.addEventListener('change', () => gruppe.apply(feld.value));
      label.htmlFor = feld.id = `sticky-atc-option-${gruppe.key.replace(/\W+/g, '-')}`;
      zeile.append(label, feld);
      this.panelInner.appendChild(zeile);
      return feld;
    });

  }


  onOutside(event) {
    if (performance.now() < (this.proxyUntil || 0)) return;
    // Beim Varianten-Wechsel tauscht das Theme sein Markup aus: das geklickte
    // Element haengt dann nicht mehr im Dokument. Das ist kein Klick daneben.
    if (!event.target.isConnected) return;
    // Alles, was die Karte bedient, landet im echten Kaufbereich. Frisch
    // gesucht, weil ein gemerkter Container nach dem Tausch losgeloest waere.
    if (this.contains(event.target)) return;
    if (event.target.closest('product-info, .product__info-container')) return;
    this.togglePanel(false);
  }

  onKey(event) {
    if (event.key === 'Escape') {
      this.togglePanel(false);
      this.button.focus();
    }
  }

  togglePanel(offen) {
    if (offen && this.panel.hidden === false && this.panelOpen) return;
    this.panelOpen = offen;
    this.panel.hidden = !offen;
    this.classList.toggle('sticky-atc--panel-open', offen);
    if (offen) {
      // Verzoegert angemeldet, sonst schliesst der oeffnende Klick sofort wieder.
      setTimeout(() => document.addEventListener('click', this.onOutside), 0);
      document.addEventListener('keydown', this.onKey);
      this.panelInner.querySelector('select')?.focus();
    } else {
      document.removeEventListener('click', this.onOutside);
      document.removeEventListener('keydown', this.onKey);
    }
    this.syncButton();
    this.avoidChatWidget();
  }

  // Wird jedes Mal aufgerufen, wenn die Quelle (neu) aufgeloest wurde — beim
  // ersten Erscheinen des Kaufbereichs ebenso wie nach einem Markup-Tausch.
  watchSource() {
    if (this.watchedButton === this.realButton) return;
    this.watchedButton = this.realButton;
    this.sourceObserver?.disconnect();
    this.sourceObserver = new MutationObserver(this.onSourceMutation);
    const options = { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled', 'class'] };
    if (this.priceSource) this.sourceObserver.observe(this.priceSource, options);
    this.sourceObserver.observe(this.realButton, options);
  }

  onSourceMutation() {
    // Der Mutation-Sturm eines Varianten-Wechsels wird zu einem Sync gebuendelt.
    clearTimeout(this.syncTimer);
    this.syncTimer = setTimeout(() => this.sync(), 50);
  }

  /* ---------- Sprungmarken ---------- */

  // Gebaut wird die Liste aus den Abschnitten, die auf DIESER Seite existieren.
  // Steht am Ende nur ein Ziel da, ist das keine Navigation und entfaellt.
  buildNav() {
    const konfig = this.querySelector('[data-sticky-atc-nav-config]');
    if (!this.nav || !konfig) return;

    let eintraege = [];
    try {
      eintraege = JSON.parse(konfig.textContent);
    } catch (fehler) {
      return;
    }

    this.navTargets = [];
    eintraege.forEach((eintrag) => {
      const ziel = eintrag.selectors.map((s) => document.querySelector(s)).find(Boolean);
      if (!ziel || this.navTargets.some((n) => n.ziel === ziel)) return;
      const punkt = document.createElement('li');
      const link = document.createElement('a');
      link.className = 'sticky-atc__nav-link';
      link.href = '#';
      link.textContent = eintrag.label;
      link.addEventListener('click', (event) => {
        event.preventDefault();
        this.springeZu(ziel);
      });
      punkt.appendChild(link);
      this.navList.appendChild(punkt);
      this.navTargets.push({ ziel, link });
    });

    if (this.navTargets.length < 2) {
      this.navTargets = [];
      this.nav.remove();
      return;
    }

    this.nav.hidden = false;
    // Auf dem Desktop sitzt die Zeile fest im Seiteninhalt unter dem
    // Kaufbereich, mobil in der Leiste. Verschoben statt doppelt gerendert,
    // damit es nur eine Liste und einen Zustand gibt.
    this.desktop = window.matchMedia('(min-width: 990px)');
    this.desktop.addEventListener('change', this.onBreakpoint);
    this.onBreakpoint();
  }

  onBreakpoint() {
    if (!this.navTargets?.length) return;
    const imInhalt = this.desktop.matches;
    this.nav.classList.toggle('sticky-atc__nav--im-inhalt', imInhalt);
    const anker = this.productInfo || this.info;
    if (imInhalt && anker?.parentElement) {
      anker.insertAdjacentElement('afterend', this.nav);
    } else if (!imInhalt && this.nav.parentElement !== this) {
      this.insertBefore(this.nav, this.querySelector('.sticky-atc__bar'));
    }
  }

  // Abstand von der Viewport-Oberkante, ab dem ein Abschnitt als "erreicht"
  // gilt: alles, was Header und eigene Leiste verdecken wuerden.
  messlinie() {
    const header = document.querySelector('.section-header');
    const kopf = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
    const eigene = this.desktop?.matches ? 0 : this.getBoundingClientRect().height;
    return kopf + eigene + 12;
  }

  springeZu(ziel) {
    const scrolle = (weich) =>
      window.scrollTo({
        top: Math.max(0, window.scrollY + ziel.getBoundingClientRect().top - this.messlinie()),
        behavior: weich && !window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'auto',
      });
    scrolle(true);
    // Nachladende Bilder verschieben die Seite waehrend des Scrollens — beim
    // Sprung zu den Empfehlungen landete man dadurch 189px zu tief. Einmal
    // nachjustieren, sobald die Seite steht; kein Dauerlauf.
    clearTimeout(this.sprungKorrektur);
    this.sprungKorrektur = setTimeout(() => {
      if (Math.abs(ziel.getBoundingClientRect().top - this.messlinie()) > 8) scrolle(false);
    }, 600);
  }

  markiereAktivenAbschnitt() {
    if (!this.navTargets?.length) return;
    // Dieselbe Linie, auf die auch gesprungen wird — sonst zeigt die
    // Hervorhebung nach einem Sprung noch auf den vorherigen Abschnitt.
    const linie = this.messlinie() + 4;
    let aktiv = this.navTargets[0];
    this.navTargets.forEach((n) => {
      if (n.ziel.getBoundingClientRect().top <= linie) aktiv = n;
    });
    if (aktiv === this.aktiverPunkt) return;
    this.aktiverPunkt = aktiv;
    this.navTargets.forEach(({ link }) => link.removeAttribute('aria-current'));
    aktiv.link.setAttribute('aria-current', 'true');
  }

  /* ---------- Ein-/Ausblenden ---------- */

  // Bewusst scroll- statt IntersectionObserver-basiert: ein Sprung im Scroll
  // (Anker-Link, wiederhergestellte Position, schnelles Wischen) kann den
  // Kaufbereich ueberspringen, ohne dass er je als sichtbar gemessen wird —
  // dann feuert der Observer keinen Schwellenwechsel und die Karte bliebe aus.
  watchVisibility() {
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    // Nachladende Bilder und App-Blocks verschieben die Seite ohne Scroll-Event.
    this.layoutObserver = new ResizeObserver(this.onScroll);
    this.layoutObserver.observe(document.body);
    // Die Hauptsektion tauscht bei Varianten- und Mengenwechseln ihr Markup aus.
    // Waehrend dieses Moments gibt es keinen Kaufbutton zu spiegeln — ohne
    // dieses Nachfassen bliebe die Karte bis zum naechsten Scrollen aus.
    this.infoObserver = new MutationObserver(this.onScroll);
    this.infoObserver.observe(this.productInfo || document.body, { childList: true, subtree: true });
    // Der B2B-Zugriffsschutz gibt die Seite frei, indem er eine Klasse an <html>
    // entfernt. Das aendert weder Groesse noch Markup — ohne diesen Beobachter
    // bliebe die Karte nach der Freigabe bis zur naechsten Scroll-Bewegung aus.
    this.rootObserver = new MutationObserver(this.onScroll);
    this.rootObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    this.updateVisibility();
  }

  onScroll() {
    if (this.scrollFrame) return;
    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = null;
      this.updateVisibility();
    });
  }

  updateVisibility() {
    this.markiereAktivenAbschnitt();
    if (this.sourceIsStale) {
      if (!this.resolveSource()) {
        // Waehrend die Sektion neu rendert, ist die Quelle kurz weg. Sofort
        // auszublenden wuerde ein offenes Panel mitten in der Bedienung
        // zuklappen — deshalb erst nach mehreren erfolglosen Versuchen.
        this.aussetzer = (this.aussetzer || 0) + 1;
        if (this.aussetzer > 10) this.toggle(false, true);
        clearTimeout(this.nachfassen);
        this.nachfassen = setTimeout(() => this.onScroll(), 100);
        return;
      }
      this.aussetzer = 0;
      // Quelle ist (wieder) da — Beobachter und Inhalte nachziehen.
      this.sync();
    }
    // Sichtbar, sobald der echte Kaufbereich nach oben aus dem Bild ist.
    this.toggle(this.anchor.getBoundingClientRect().bottom < 0);
  }

  toggle(visible, erzwungen = false) {
    // Solange das Panel offen ist, bleibt die Karte stehen. Ein Varianten-
    // Wechsel rendert die Sektion neu und verschiebt die Seite; der Kaufbereich
    // rutscht dabei kurz zurueck ins Bild und wuerde die Karte samt geoeffnetem
    // Panel einfahren lassen — mitten in der Bedienung.
    if (!visible && this.panelOpen && !erzwungen) return;
    // Fremd-Widgets am unteren Rand richten sich nach der tatsaechlichen
    // Leistenhoehe — die schwankt je nach Breite zwischen zwei und drei Zeilen.
    document.documentElement.style.setProperty('--sticky-atc-hoehe', `${this.offsetHeight}px`);
    // Abstand vom unteren Rand bis knapp ueber die Karte: daran richten sich
    // Widgets aus, die sonst auf ihr liegen (Trusted-Shops-Siegel).
    const oben = Math.round(window.innerHeight - this.getBoundingClientRect().top + 12);
    document.documentElement.style.setProperty('--sticky-atc-ueber', `${Math.max(0, oben)}px`);
    if (this.visible !== visible) {
      this.visible = visible;
      this.classList.toggle('sticky-atc--visible', visible);
      this.setAttribute('aria-hidden', visible ? 'false' : 'true');
      document.body.classList.toggle('sticky-atc-visible', visible);
      if (!visible && this.panelOpen) this.togglePanel(false);
    }
    // Nach dem Zustandswechsel, damit der Chat-Button den richtigen Abstand bekommt.
    this.avoidChatWidget();
  }

  // Unten rechts sitzt ein Chat-Button. Auf dem Desktop ist die Karte schmal
  // genug, um sich darueber zu legen; mobil laeuft die Leiste ueber die volle
  // Breite und wuerde ihn verdecken — dann weicht der Button aus.
  avoidChatWidget() {
    const shadow = document.querySelector('shopify-chat')?.shadowRoot;
    const chat = shadow?.querySelector('.activator') || document.querySelector('#fc_frame');
    const rect = chat?.getBoundingClientRect();
    const vorhanden = !!rect?.height;
    const mobil = !window.matchMedia('(min-width: 990px)').matches;

    const eigenerAbstand = vorhanden && !mobil ? Math.max(0, Math.round(window.innerHeight - rect.top)) : 0;
    if (eigenerAbstand !== this.chatOffset) {
      this.chatOffset = eigenerAbstand;
      this.style.setProperty('--sticky-atc-chat', `${eigenerAbstand}px`);
    }

    // Shopify Inbox rendert seinen Button in einem Shadow Root, an den kein
    // Stylesheet des Themes herankommt — die Regel wird deshalb dort abgelegt
    // und beim Ausblenden wieder geleert.
    if (!shadow) return;
    const abstand = vorhanden && mobil && this.visible ? Math.round(this.offsetHeight + 12) : 0;
    if (abstand === this.chatShift) return;
    this.chatShift = abstand;
    let style = shadow.getElementById('sticky-atc-chat-offset');
    if (!style) {
      style = document.createElement('style');
      style.id = 'sticky-atc-chat-offset';
      shadow.appendChild(style);
    }
    style.textContent = abstand ? `.activator, .activator-nudge { bottom: ${abstand}px !important; }` : '';
  }
}

// Was der Shop im Kaufbereich verbirgt, darf die Karte nicht zeigen. Der
// B2B-Zugriffsschutz haengt bis zur Anmeldung eine Klasse an <html>, die die
// ganze Seite unsichtbar schaltet — die Karte wuerde sonst als einziges Element
// Preis und Kauf-Button darueber stehen lassen.
// Beschriftungen an der Wortgrenze kuerzen — mitten im Wort abzuschneiden
// ("… direkt kürze") sieht nach Fehler aus.
function kuerze(text, laenge = 48) {
  const sauber = (text || '').replace(/\s+/g, ' ').trim();
  if (sauber.length <= laenge) return sauber;
  const gekuerzt = sauber.slice(0, laenge);
  const luecke = gekuerzt.lastIndexOf(' ');
  return `${(luecke > laenge * 0.6 ? gekuerzt.slice(0, luecke) : gekuerzt).replace(/[\s,;:.!?-]+$/, '')}…`;
}

// Der sichtbare Text eines Elements: Das Theme haengt an Varianten-Pillen eine
// Notiz fuer Screenreader ("Variante ausverkauft oder nicht verfuegbar"), die
// sonst ohne Leerzeichen am Wert klebt.
function sichtbarerText(element) {
  if (!element) return '';
  const kopie = element.cloneNode(true);
  kopie.querySelectorAll('.visually-hidden, [hidden], svg').forEach((n) => n.remove());
  return kopie.textContent.replace(/\s+/g, ' ').trim();
}

function isRendered(element) {
  const styles = getComputedStyle(element);
  return styles.visibility !== 'hidden' && styles.display !== 'none' && element.offsetParent !== null;
}

customElements.define('sticky-atc', StickyAtc);
